/* A SAVE OF A COMPANY-WIDE QUOTA SETTINGS ROW IS REFUSED IF SOMEONE ELSE SAVED IT SINCE THIS SCREEN LOADED (2026-10-01).

   ae_quota_settings and company_quota_settings are ONE row per company, and Revenue Build and Deferred Revenue send their
   whole owned field set from whatever copy the screen holds -- so a second user's save from a screen loaded before the
   first user's save used to revert the first user's change silently (diffs/settings-concurrency-build.txt).

   The server returns `stamp` (the row's updated_at as the DATABASE'S OWN TEXT -- a JS Date would lose the microseconds and
   shift by the timezone, so it would never compare equal) on the GET and on every successful PUT, and refuses a PUT whose
   `expected_stamp` is not the stored one with 409 `changed_by_someone_else`.

   THE RULE, one holder per row per page:
     - the held stamp is the model's (rawData.aeqSettings.stamp / rawData.cqSettings.stamp), refreshed whenever the model
       refetches the row;
     - after this page's OWN successful save it adopts the stamp the server returned, so the same user's next save (a
       toggle, then Save) is never refused by their first;
     - a row that carries no `stamp` at all (an older server) sends no expected_stamp, i.e. today's behaviour. A `null`
       stamp is the fabricated default (no row yet) and IS sent: if someone else creates the row meanwhile, it is refused. */
import { useCallback, useEffect, useMemo, useRef } from 'react';

export const STAMP_CONFLICT = 'changed_by_someone_else';

export function useSettingsStamp(row) {
  const held = useRef(row && Object.prototype.hasOwnProperty.call(row, 'stamp') ? row.stamp : undefined);
  useEffect(() => {
    held.current = row && Object.prototype.hasOwnProperty.call(row, 'stamp') ? row.stamp : undefined;
  }, [row]);
  // The payload with the held stamp added -- or unchanged when there is none to send.
  const withStamp = useCallback(payload => (held.current === undefined ? payload : { ...payload, expected_stamp: held.current }), []);
  // After a successful save: take the stamp the server returned (an axios response or the row itself).
  const adopt = useCallback(res => {
    const savedRow = res && res.data !== undefined ? res.data : res;
    if (savedRow && Object.prototype.hasOwnProperty.call(savedRow, 'stamp')) held.current = savedRow.stamp;
  }, []);
  // One stable object, so a save callback can list it as a dependency without changing on every render.
  return useMemo(() => ({ withStamp, adopt, held }), [withStamp, adopt]);
}

// The message to show beside Save when a save was refused because someone else saved first; null for any other error.
export function stampConflictMessage(err) {
  const d = err && err.response && err.response.data;
  if (!d || d.error !== STAMP_CONFLICT) return null;
  return d.message || 'These settings were changed by someone else since you opened this page. Reload to see their change, then make yours.';
}
